/**
 * Full Forma data pull + API verification.
 *
 * Exercises every read surface of the live Forma SDK we depend on and assembles
 * a single `forma-full-snapshot/v1` payload, while recording a per-call API
 * report (ok / error / unavailable) so an operator can verify exactly which SDK
 * calls work against their real model. Every call is wrapped so one failing
 * surface never aborts the pull — the snapshot carries whatever succeeded and
 * the report explains the rest.
 *
 * This is read-only and screening-neutral: it pulls data and reports
 * connectivity; it triggers no analyses and asserts no compliance.
 */

import type {
  FormaAnalysisRecord,
  FormaAreaMetrics,
  FormaFootprint,
  FormaGeometryDetailClient,
  FormaProjectMetadata,
  FormaWindParameters,
} from './index';

/** Default geometry categories enumerated during a full pull. */
export const DEFAULT_PULL_CATEGORIES = [
  'building',
  'buildings',
  'terrain',
  'site_limit',
  'roads',
  'road',
  'water',
  'green',
] as const;

/** Default cap on per-element footprint/mesh fetches to bound a pull. */
export const DEFAULT_MAX_DETAIL_ELEMENTS = 50;

/** Live Forma SDK surfaces consumed by a full pull. Optional ones are reported as unavailable when missing. */
export interface FormaFullPullClient {
  getProjectId(): string;
  project: {
    get(): Promise<FormaProjectMetadata>;
    getGeoLocation(): Promise<[number, number] | undefined>;
    getCountryCode?(): Promise<string | undefined>;
    getTimezone?(): Promise<string | undefined>;
  };
  proposal: {
    getId(): Promise<string>;
    getRootUrn(): Promise<string>;
  };
  selection: {
    getSelection(): Promise<string[]>;
  };
  geometry: FormaGeometryDetailClient;
  elements?: {
    getByPath(request: {
      path: string;
      rootUrn?: string;
      recursive?: boolean;
    }): Promise<{ element?: unknown }>;
  };
  areaMetrics?: {
    calculate(request: { paths?: string[] }): Promise<FormaAreaMetrics>;
  };
  analysis?: {
    list(request: {
      authcontext?: string;
      elementUrnPrefix?: string;
      analysisTypes?: string[];
    }): Promise<FormaAnalysisRecord[]>;
  };
  prediction?: {
    getWindParameters(): Promise<FormaWindParameters>;
  };
  sun?: {
    getDate(): Promise<Date>;
  };
}

export interface PullFullFormaSnapshotOptions {
  forma: FormaFullPullClient;
  /** Geometry categories to enumerate. Defaults to {@link DEFAULT_PULL_CATEGORIES}. */
  categories?: readonly string[];
  /** Cap on per-element footprint/mesh fetches. Defaults to {@link DEFAULT_MAX_DETAIL_ELEMENTS}. */
  maxDetailElements?: number;
  /** ISO timestamp stamped onto the snapshot. The pure function does not read the clock. */
  pulledAt?: string;
}

export type FormaApiCallStatus = 'ok' | 'error' | 'unavailable';

export interface FormaApiCallReport {
  surface: string;
  status: FormaApiCallStatus;
  count?: number;
  error?: string;
}

export interface FormaGeometryCategorySnapshot {
  category: string;
  paths: string[];
}

export interface FormaElementSnapshot {
  path: string;
  footprint?: FormaFootprint;
  mesh?: { triangleCount: number };
  /** Element metadata (properties, type) from elements.getByPath, when available. */
  element?: unknown;
}

export interface FormaFullSnapshot {
  schemaVersion: 'forma-full-snapshot/v1';
  pulledAt?: string;
  project: {
    formaProjectId?: string;
    metadata?: FormaProjectMetadata;
    countryCode?: string;
    timezone?: string;
    geoLocation?: { latitude: number; longitude: number };
  };
  proposal: {
    id?: string;
    rootUrn?: string;
  };
  selection: {
    selectedPaths: string[];
  };
  geometry: {
    categories: FormaGeometryCategorySnapshot[];
    elements: FormaElementSnapshot[];
    detailTruncated: boolean;
  };
  areaMetrics?: FormaAreaMetrics;
  nativeAnalyses: FormaAnalysisRecord[];
  windParameters?: FormaWindParameters;
  sunDate?: { iso: string };
}

export interface FullFormaPullResult {
  snapshot: FormaFullSnapshot;
  apiReport: FormaApiCallReport[];
  okCount: number;
  errorCount: number;
  unavailableCount: number;
}

/**
 * Pulls a full snapshot of the live Forma model across every read surface we
 * consume, returning the assembled data plus a per-call API verification report.
 * Never throws: each surface is attempted independently and recorded.
 */
export async function pullFullFormaSnapshot(
  options: PullFullFormaSnapshotOptions,
): Promise<FullFormaPullResult> {
  const { forma } = options;
  const categories = options.categories ?? DEFAULT_PULL_CATEGORIES;
  const maxDetailElements = options.maxDetailElements ?? DEFAULT_MAX_DETAIL_ELEMENTS;
  const report: FormaApiCallReport[] = [];

  const snapshot: FormaFullSnapshot = {
    schemaVersion: 'forma-full-snapshot/v1',
    ...(options.pulledAt === undefined ? {} : { pulledAt: options.pulledAt }),
    project: {},
    proposal: {},
    selection: { selectedPaths: [] },
    geometry: { categories: [], elements: [], detailTruncated: false },
    nativeAnalyses: [],
  };

  const formaProjectId = safeSync(report, 'project.getProjectId', () => forma.getProjectId());
  if (formaProjectId !== undefined) {
    snapshot.project.formaProjectId = formaProjectId;
  }

  const metadata = await attempt(report, 'project.get', () => forma.project.get());
  if (metadata !== undefined) {
    snapshot.project.metadata = metadata;
  }

  const geoLocation = await attempt(report, 'project.getGeoLocation', () =>
    forma.project.getGeoLocation(),
  );
  if (geoLocation !== undefined) {
    snapshot.project.geoLocation = { latitude: geoLocation[0], longitude: geoLocation[1] };
  }

  if (forma.project.getCountryCode !== undefined) {
    const getCountryCode = forma.project.getCountryCode.bind(forma.project);
    const countryCode = await attempt(report, 'project.getCountryCode', () => getCountryCode());
    if (countryCode !== undefined) {
      snapshot.project.countryCode = countryCode;
    }
  } else {
    report.push({ surface: 'project.getCountryCode', status: 'unavailable' });
  }

  if (forma.project.getTimezone !== undefined) {
    const getTimezone = forma.project.getTimezone.bind(forma.project);
    const timezone = await attempt(report, 'project.getTimezone', () => getTimezone());
    if (timezone !== undefined) {
      snapshot.project.timezone = timezone;
    }
  } else {
    report.push({ surface: 'project.getTimezone', status: 'unavailable' });
  }

  const proposalId = await attempt(report, 'proposal.getId', () => forma.proposal.getId());
  if (proposalId !== undefined) {
    snapshot.proposal.id = proposalId;
  }

  const rootUrn = await attempt(report, 'proposal.getRootUrn', () => forma.proposal.getRootUrn());
  if (rootUrn !== undefined) {
    snapshot.proposal.rootUrn = rootUrn;
  }

  const selection = await attempt(report, 'selection.getSelection', () =>
    forma.selection.getSelection(),
  );
  if (selection !== undefined) {
    snapshot.selection.selectedPaths = selection;
    setCount(report, 'selection.getSelection', selection.length);
  }

  await pullGeometry(
    forma,
    rootUrn,
    categories,
    maxDetailElements,
    selection ?? [],
    snapshot,
    report,
  );

  await pullAreaMetrics(forma, snapshot, report);
  await pullNativeAnalyses(forma, snapshot, report);
  await pullWindParameters(forma, snapshot, report);
  await pullSunDate(forma, snapshot, report);

  return {
    snapshot,
    apiReport: report,
    okCount: report.filter((entry) => entry.status === 'ok').length,
    errorCount: report.filter((entry) => entry.status === 'error').length,
    unavailableCount: report.filter((entry) => entry.status === 'unavailable').length,
  };
}

async function pullGeometry(
  forma: FormaFullPullClient,
  rootUrn: string | undefined,
  categories: readonly string[],
  maxDetailElements: number,
  selection: readonly string[],
  snapshot: FormaFullSnapshot,
  report: FormaApiCallReport[],
): Promise<void> {
  const collectedPaths = new Set<string>();

  for (const category of categories) {
    const request = rootUrn === undefined ? { category } : { category, urn: rootUrn };
    const paths = await attempt(report, `geometry.getPathsByCategory(${category})`, () =>
      forma.geometry.getPathsByCategory(request),
    );
    if (paths !== undefined) {
      snapshot.geometry.categories.push({ category, paths });
      setCount(report, `geometry.getPathsByCategory(${category})`, paths.length);
      paths.forEach((path) => collectedPaths.add(path));
    }
  }

  // Selection can contain duplicate paths; dedupe so each element is reported
  // once (duplicate surfaces would collide React keys in the UI and confuse the
  // per-surface count lookup).
  const detailSource = [...new Set(selection.length > 0 ? selection : [...collectedPaths])];
  const detailPaths = detailSource.slice(0, maxDetailElements);
  snapshot.geometry.detailTruncated = detailSource.length > detailPaths.length;

  // Element metadata is an optional surface; report it unavailable once rather
  // than per path so the report does not fill with duplicate rows.
  const elementsClient = forma.elements;
  if (elementsClient === undefined) {
    report.push({ surface: 'elements.getByPath', status: 'unavailable' });
  }

  for (const path of detailPaths) {
    const footprint = await attempt(report, `geometry.getFootprint(${path})`, () =>
      forma.geometry.getFootprint(rootUrn === undefined ? { path } : { path, urn: rootUrn }),
    );
    const triangles = await attempt(report, `geometry.getTriangles(${path})`, () =>
      forma.geometry.getTriangles(rootUrn === undefined ? { path } : { path, urn: rootUrn }),
    );
    const detail =
      elementsClient === undefined
        ? undefined
        : await attempt(report, `elements.getByPath(${path})`, () =>
            elementsClient.getByPath(rootUrn === undefined ? { path } : { path, rootUrn }),
          );
    const element: FormaElementSnapshot = { path };
    if (footprint !== undefined) {
      element.footprint = footprint;
    }
    if (triangles !== undefined) {
      element.mesh = { triangleCount: Math.floor(triangles.length / 9) };
    }
    if (detail?.element !== undefined) {
      element.element = detail.element;
    }
    snapshot.geometry.elements.push(element);
  }
}

async function pullAreaMetrics(
  forma: FormaFullPullClient,
  snapshot: FormaFullSnapshot,
  report: FormaApiCallReport[],
): Promise<void> {
  if (forma.areaMetrics === undefined) {
    report.push({ surface: 'areaMetrics.calculate', status: 'unavailable' });
    return;
  }

  const areaMetrics = forma.areaMetrics;
  const metrics = await attempt(report, 'areaMetrics.calculate', () => areaMetrics.calculate({}));
  if (metrics !== undefined) {
    snapshot.areaMetrics = metrics;
  }
}

async function pullNativeAnalyses(
  forma: FormaFullPullClient,
  snapshot: FormaFullSnapshot,
  report: FormaApiCallReport[],
): Promise<void> {
  if (forma.analysis === undefined) {
    report.push({ surface: 'analysis.list', status: 'unavailable' });
    return;
  }

  const analysis = forma.analysis;
  const analyses = await attempt(report, 'analysis.list', () => analysis.list({}));
  if (analyses !== undefined) {
    snapshot.nativeAnalyses = analyses;
    setCount(report, 'analysis.list', analyses.length);
  }
}

async function pullWindParameters(
  forma: FormaFullPullClient,
  snapshot: FormaFullSnapshot,
  report: FormaApiCallReport[],
): Promise<void> {
  if (forma.prediction === undefined) {
    report.push({ surface: 'prediction.getWindParameters', status: 'unavailable' });
    return;
  }

  const prediction = forma.prediction;
  const windParameters = await attempt(report, 'prediction.getWindParameters', () =>
    prediction.getWindParameters(),
  );
  if (windParameters !== undefined) {
    snapshot.windParameters = windParameters;
  }
}

async function pullSunDate(
  forma: FormaFullPullClient,
  snapshot: FormaFullSnapshot,
  report: FormaApiCallReport[],
): Promise<void> {
  if (forma.sun === undefined) {
    report.push({ surface: 'sun.getDate', status: 'unavailable' });
    return;
  }

  const sun = forma.sun;
  const date = await attempt(report, 'sun.getDate', () => sun.getDate());
  if (date !== undefined) {
    snapshot.sunDate = { iso: date.toISOString() };
  }
}

async function attempt<Value>(
  report: FormaApiCallReport[],
  surface: string,
  call: () => Promise<Value>,
): Promise<Value | undefined> {
  try {
    const value = await call();
    report.push({ surface, status: 'ok' });
    return value;
  } catch (error) {
    report.push({ surface, status: 'error', error: describeError(error) });
    return undefined;
  }
}

function safeSync<Value>(
  report: FormaApiCallReport[],
  surface: string,
  call: () => Value,
): Value | undefined {
  try {
    const value = call();
    report.push({ surface, status: 'ok' });
    return value;
  } catch (error) {
    report.push({ surface, status: 'error', error: describeError(error) });
    return undefined;
  }
}

function setCount(report: FormaApiCallReport[], surface: string, count: number): void {
  const entry = report.find((item) => item.surface === surface);
  if (entry !== undefined && entry.status === 'ok') {
    entry.count = count;
  }
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
